<template>
  <main id="pages-quick-order-order-by-name-content" class="ragnarok">
    <section class="banner">
      <div class="container">
        <nav class="breadcrumb" aria-label="Navigation breadcrumb">
          <ul>
            <li><a href="/" aria-label="Home Page">Home</a></li>
            <li><a href="/products">Shop</a></li>
            <li><a href="/quick-order">Quick Order</a></li>
          </ul>
        </nav>
        <h1 class="title is-1">Quick Order</h1>
      </div>
    </section>

    <section class="spacer">

    </section>

    <two-column-layout>
      <template #left>
        <div class="container">
          <div class="order-form">
            <div class="search-form">
              <div class="columns">
                <div class="column">
                  <b-field>
                    <b-input v-model="search"
                             icon-pack="fas"
                             icon="search"
                             :loading="loading"
                             placeholder="Find product..."></b-input>
                  </b-field>
                </div>
              </div>
            </div>
            <div class="headings">
              <div class="columns is-mobile is-multiline is-vcentered">
                <div class="column is-2-desktop is-4-tablet is-12-mobile">
                  <b-button type="is-black" :loading="saving" @click="addToCart">Add to Cart</b-button>
                </div>
                <div class="column is-hidden-desktop is-hidden-tablet is-6-mobile">
                </div>
                <div class="column is-7-desktop is-6-tablet is-6-mobile"><a role="button" tabindex="0" @click="nameSort = !nameSort">Name
                  <b-icon pack="fas" :icon="nameSort ? 'caret-down' : 'caret-up'"></b-icon>
                </a></div>
                <div class="column is-3-desktop is-2-tablet is-hidden-mobile">Status</div>
              </div>
            </div>
            <div class="product" v-for="product in filteredProducts">
              <div class="columns is-mobile is-multiline is-vcentered">
                <div class="column is-1-desktop is-2-tablet is-3-mobile" v-if="product.showInput">
                  <b-input v-model="product.quantity" :has-counter="false" :maxlength="3" :lazy="true"></b-input>
                </div>
                <div class="column is-1-desktop is-2-tablet is-3-mobile " v-else>
                  <b-dropdown aria-role="list" :disabled="!product.orderable">
                    <div class="button" slot="trigger" slot-scope="{ active }">
                      <span>{{ product.quantity }}</span>
                      <span class="icon is-small">
                        <i class="fas" :class="active ? 'fa-angle-up' : 'fa-angle-down'"></i>
                      </span>
                    </div>
                    <b-dropdown-item v-for="(n, i) in 20" :key="i"
                                     aria-role="listitem"
                                     @click="product.quantity = i">{{ i }}
                    </b-dropdown-item>
                    <b-dropdown-item aria-role="listitem"
                                     @click="showInput(product)">20+
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
                <div class="column is-1-desktop is-2-tablet is-3-mobile ">
                  <image-tile :image="product.primaryImage"></image-tile>
                </div>
                <div class="column is-7-desktop is-6-tablet is-6-mobile">
                  <div>{{ product.name }}</div>
                  <div class="sku">{{ product.sku }}</div>
                </div>
                <div class="column is-3-desktop is-2-tablet is-12-mobile">
                  <div class="flex-middle" v-if="!product.extendedBackorder">
                    <span class="status-dot" :class="{inactive : !product.orderable, 'out-of-stock' : product.outOfStock, 'discontinued' : (product.status === 'Discontinued')}"></span>
                    {{ product.status }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #right>
        <div class="title is-3">
          <b-icon pack="fas" icon="rabbit-fast"></b-icon>
          What's Quick Order?
        </div>
        <p>Quickly and easily add products to your cart. Previously ordered products can be quickly reordered by visiting your <a href="/account/orders">order history</a> page.</p>
      </template>
    </two-column-layout>
  </main>
</template>

<script>
import NumberSpinner from "~/components/NumberSpinner.vue";
import AddToCartButton from "~/components/AddToCartButton.vue";
import TwoColumnLayout from "~/components/global/TwoColumnLayout.vue";
import ImageTile from "~/components/global/ImageTile.vue";
import debounce from "lodash.debounce";
import OnDemandLiveRegion from "on-demand-live-region";

export default {
  name: "QuickOrderPage",

  components: {
    NumberSpinner, AddToCartButton, TwoColumnLayout, ImageTile
  },

  data() {
    return {
      products: pageData || [],
      filteredProducts: [],
      saving: false,
      search: "",
      nameSort: false,
      loading: false
    }
  },

  beforeMount() {
    this.products.forEach(product => product.showInput = false);
    this.updateProducts();
  },

  methods: {
    addToCart() {
      const itemsToAdd = [];

      this.products.forEach(product => {
        if (product.quantity > 0) {
          itemsToAdd.push({
            productId: product.productId,
            quantity: product.quantity
          });
        }
      });

      // Save 'em
      if (itemsToAdd.length > 0) {
        this.saving = true;

        axios.patch("/cart/items", {items: itemsToAdd})
            .then(response => {
              this.saving = false;
              const message = `${itemsToAdd.length} product(s) added to <a href="/cart">your cart</a>`;
              this.$buefy.notification.open({duration: 5000, type: 'is-primary', position: 'is-bottom-right', message: message, role: "alert"})
              new OnDemandLiveRegion().say(message)
            })
            .catch(error => {
              this.saving = false;
            });
      }
    },

    showInput(product) {
      product.showInput = true;
      this.updateProducts();
    },

    async updateProducts() {
      this.loading = true;
      new Promise(_ => {
        let filteredProducts = this.products.filter(product => product.name.toLowerCase()
            .includes(this.search.toLowerCase()) || product.sku.toLowerCase()
            .includes(this.search.toLowerCase()));

        filteredProducts = filteredProducts.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        if (this.nameSort) filteredProducts = filteredProducts.reverse();
        this.filteredProducts = filteredProducts;

        this.loading = false;
      })
    }
  },

  watch: {
    search: debounce(function () {
      this.updateProducts();
    }, 1000),

    nameSort: debounce(function () {
      this.updateProducts();
    }, 1000)
  }
}
</script>

<style lang="scss" scoped>
// To do after Nuxt, do not have variables and mixins accessible
</style>